import React from "react"

import { format } from "date-fns"

import { DAY_LABELS, DAY_NAMES } from "@/lib/collectionCoverage"
import { formatDayNumeric } from "@/lib/dateFormats"
import { formatCost } from "@/lib/routeCostCalculation"
import { cn } from "@/lib/utils"
import { DaySummary } from "@/lib/weeklyPlan"

interface SummaryRowProps {
  label: string
  bold?: boolean
  borderTop?: boolean
  callout?: boolean
  weekDays: Date[]
  dailySummaries: Record<string, DaySummary>
  getValue: (ds: DaySummary) => string
  weekValue: string
  align?: "center" | "right"
  valueClassName?: (ds: DaySummary) => string | undefined
  weekClassName?: string
  onCellClick?: (payload: { dateStr: string | null; dayLabel: string }) => void
}

export const SummaryRow: React.FC<SummaryRowProps> = ({
  label,
  bold,
  borderTop,
  callout,
  weekDays,
  dailySummaries,
  getValue,
  weekValue,
  align = "center",
  valueClassName,
  weekClassName,
  onCellClick,
}) => {
  const cellBg = callout ? "bg-palette-jungleGreen000" : "bg-white"
  const textSize = callout ? "text-base" : "text-xs"
  const cellPy = callout ? "py-3" : "py-2"
  const clickable = !!onCellClick
  return (
    <tr
      className={cn(
        "border-t border-palette-jungleGreen200",
        cellBg,
        borderTop && "border-t-2 border-t-palette-jungleGreen400",
      )}
    >
      <td
        colSpan={2}
        className={cn(
          "sticky left-0 z-10 px-3 border-r border-palette-jungleGreen200",
          cellPy,
          textSize,
          cellBg,
          bold
            ? "font-bold text-palette-jungleGreen900"
            : "font-semibold text-palette-jungleGreen700",
        )}
      >
        {label}
      </td>
      {weekDays.map((day, i) => {
        const dateStr = format(day, "yyyy-MM-dd")
        const ds = dailySummaries[dateStr]
        const val = ds ? getValue(ds) : "—"
        const extra = ds && valueClassName ? valueClassName(ds) : undefined
        return (
          <td
            key={i}
            className={cn(
              "px-3 border-r border-palette-jungleGreen200 tabular-nums",
              cellPy,
              textSize,
              cellBg,
              align === "center" ? "text-center" : "text-right",
              bold ? "font-bold text-palette-jungleGreen900" : "text-palette-jungleGreen700",
              extra,
              clickable && "cursor-pointer hover:bg-green-100/50 transition-colors",
            )}
            onClick={
              clickable
                ? () =>
                    onCellClick!({ dateStr, dayLabel: `${DAY_LABELS[DAY_NAMES[i]]} ${formatDayNumeric(day)}` })
                : undefined
            }
          >
            {val}
          </td>
        )
      })}
      <td
        className={cn(
          "px-3 font-semibold tabular-nums",
          cellPy,
          textSize,
          align === "center" ? "text-center" : "text-right",
          bold ? "font-bold text-palette-jungleGreen900" : "text-palette-jungleGreen900",
          callout ? "bg-palette-jungleGreen200" : "bg-palette-jungleGreen100",
          weekClassName,
          clickable && "cursor-pointer hover:bg-green-100/50 transition-colors",
        )}
        onClick={clickable ? () => onCellClick!({ dateStr: null, dayLabel: "Week" }) : undefined}
      >
        {weekValue}
      </td>
    </tr>
  )
}

interface NetTotalRowProps {
  compact?: boolean
  weekDays: Date[]
  dailySummaries: Record<string, DaySummary>
  weekTotals: DaySummary
}

export const NetTotalRow: React.FC<NetTotalRowProps> = ({
  compact,
  weekDays,
  dailySummaries,
  weekTotals,
}) => {
  const netPy = compact ? "py-2" : "py-3.5"
  const netText = compact ? "text-sm" : "text-base"
  return (
    <tr className="bg-purple-100 border-t-2 border-purple-300">
      <td
        colSpan={2}
        className={cn(
          "sticky left-0 z-10 bg-purple-100 px-3 font-bold text-purple-900 border-r border-purple-200",
          netPy,
          netText,
        )}
      >
        TOTAL COST (Net)
      </td>
      {weekDays.map((day, i) => {
        const ds = dailySummaries[format(day, "yyyy-MM-dd")]
        const net = ds ? ds.totalCost - ds.haulageRevenue : 0
        return (
          <td
            key={i}
            className={cn(
              "text-center px-3 font-bold text-purple-900 border-r border-purple-200 tabular-nums",
              netPy,
              netText,
            )}
          >
            {formatCost(net)}
          </td>
        )
      })}
      <td
        className={cn(
          "text-center px-3 font-bold text-purple-900 bg-purple-200 tabular-nums",
          netPy,
          netText,
        )}
      >
        {formatCost(weekTotals.totalCost - weekTotals.haulageRevenue)}
      </td>
    </tr>
  )
}
